import type { HybridspaceBookAWorkspaceProps } from './components/HybridspaceBookAWorkspace/HybridspaceBookAWorkspace';
import type { WorkspaceBookingListProps } from './components/WorkspaceBookingList';
import type { LocationChangeDialogProps } from './components/LocationChangeDialog';
import type { HybridspaceMoreReservedSpacesProps } from './components/HybridspaceMoreReservedSpaces';
import type { HybridspaceReservedSpacesInBuildingProps } from './components/HybridspaceReservedSpacesInBuilding';
import type { HybridspaceReservedSpacesProps } from './components/HybridspaceReservedSpaces';
import type { HybridspaceQuickDeskBookingProps } from './components/HybridspaceQuickDeskBooking';
import type { HybridspaceWorkspaceProps } from './components/HybridspaceWorkspace';
import type { DeskBookingSuggestionProps } from './components/DeskBookingSuggestion';
import type * as lazyIndex from './lazyIndex';
import {
    createLazyComponent,
    LazyAction,
    type LazyComponent,
    LazyImport,
    LazyModule,
} from 'owa-bundling';

const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule(
    () => import(/* webpackChunkName: "DeskBooking"*/ './lazyIndex'),
    { name: 'DeskBooking' }
);

export const DeskBookingSuggestion: LazyComponent<DeskBookingSuggestionProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.DeskBookingSuggestion);
export const HybridspaceWorkspace: LazyComponent<HybridspaceWorkspaceProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.HybridspaceWorkspace);
export const HybridspaceQuickDeskBooking: LazyComponent<
    HybridspaceQuickDeskBookingProps,
    unknown,
    void
> = createLazyComponent(lazyModule, m => m.HybridspaceQuickDeskBooking);
export const HybridspaceReservedSpaces: LazyComponent<
    HybridspaceReservedSpacesProps,
    unknown,
    void
> = createLazyComponent(lazyModule, m => m.HybridspaceReservedSpaces);
export const HybridspaceReservedSpacesInBuilding: LazyComponent<
    HybridspaceReservedSpacesInBuildingProps,
    unknown,
    void
> = createLazyComponent(lazyModule, m => m.HybridspaceReservedSpacesInBuilding);
export const HybridspaceMoreReservedSpaces: LazyComponent<
    HybridspaceMoreReservedSpacesProps,
    unknown,
    void
> = createLazyComponent(lazyModule, m => m.HybridspaceMoreReservedSpaces);
export const LocationChangeDialog: LazyComponent<LocationChangeDialogProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.LocationChangeDialog);
export const WorkspaceBookingList: LazyComponent<WorkspaceBookingListProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.WorkspaceBookingList);
export const HybridspaceBookAWorkspace: LazyComponent<
    HybridspaceBookAWorkspaceProps,
    unknown,
    void
> = createLazyComponent(lazyModule, m => m.HybridspaceBookAWorkspace);

export const lazyGetDeskSuggestionForDay: LazyImport<
    typeof lazyIndex.getDeskSuggestionForDay,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.getDeskSuggestionForDay);

export const lazyInitializeDeskBookingSuggestion: LazyAction<
    typeof lazyIndex.initializeDeskBookingSuggestion,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.initializeDeskBookingSuggestion);

export const lazyGetWorkspaceReservationsInBuildingForDay: LazyImport<
    typeof lazyIndex.getWorkspaceReservationsInBuildingForDay,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.getWorkspaceReservationsInBuildingForDay);

export const lazyBookDesk: LazyImport<typeof lazyIndex.bookDesk, typeof lazyModule> =
    new LazyImport(lazyModule, m => m.bookDesk);

export const lazyRefreshBookStatus: LazyImport<
    typeof lazyIndex.refreshBookStatus,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.refreshBookStatus);

export const lazyLoadWorkspaceReservations: LazyImport<
    typeof lazyIndex.loadWorkspaceReservations,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.loadWorkspaceReservations);

export const lazyGetWorkspaceReservationsForDay: LazyImport<
    typeof lazyIndex.getWorkspaceReservationsForDay,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.getWorkspaceReservationsForDay);

export const lazyGenerateCollaboratorSubtitle: LazyImport<
    typeof lazyIndex.generateCollaboratorSubtitle,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.generateCollaboratorSubtitle);
